'use client';

import { useTranslation } from 'next-i18next';
import { useExtendedSearchParams, useExtendedRouter } from '@hooks/next-routing-wrappers';
import { autoLoginAction } from '@auth/actions/autoLoginAction';
import { BaseAuthForm } from '@auth/forms/BaseAuthForm';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { LinkStyled, TailwindSpinner } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';

/** Types */
interface AutoLoginFormProps {
  locale: Locale;
  className?: string;
  loginToken?: string;
}

/**
 * @description 'Auto login' form component.
 * - `locale`: The locale to use for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 */
export const AutoLoginForm = (props: AutoLoginFormProps) => {
  // Destructure props
  const {
    locale,
    className = '',
    loginToken,
  } = props;

  // Helpers
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const { searchParamsAsObject } = useExtendedSearchParams();
  const iconSuccessClass = 'mb-4 mx-auto size-17 rounded-full bg-radial-gradient from-surface from-0% via-secondary/30 via-60% to-secondary/30 text-secondary ring-6 ring-secondary/10';
  const iconErrorClass = 'mb-4 mx-auto size-17 rounded-full bg-radial-gradient from-surface from-0% via-error/30 via-60% to-error/30 text-error ring-6 ring-error/10';

  function getFirstRealSegment(path: string): string | null {
    const parts = path.split('/').filter(Boolean); // removes empty segments

    if (parts.length === 0) return null;

    // If first segment is a locale (2 letters), skip it
    const isLocale = /^[a-zA-Z]{2}$/.test(parts[0]);
    return isLocale ? parts[1] : parts[0];
  }

  // Check for targetPath in URL for redirect on error
  const getTargetPath = () => {
    if (typeof searchParamsAsObject?.targetPath === 'string' && searchParamsAsObject?.targetPath && getFirstRealSegment(searchParamsAsObject?.targetPath) === getFirstRealSegment(getI18nLinkHref('/job/slug', locale))) {
      return searchParamsAsObject.targetPath;
    }
    return null;
  };

  // Render content
  // *************************
  return (
    <BaseAuthForm
      locale={locale}
      formAction={autoLoginAction}
      userToken={loginToken}
      className={className}
      withClientValidation={false}
      autoSubmit
    >
      {/* Render props from the BaseAuthForm component */}
      {({
        formState,
        isPending,
      }) => {
        // Handle redirect on error if targetPath exists
        if (formState.errorText) {
          const targetPath = getTargetPath();
          if (targetPath) {
            void router.push(targetPath);
            return null; // Don't render error UI if redirecting
          }
        }

        return (
          <>
            {/* Loading element, initial */}
            {(isPending || (!formState.success && !formState.errorText)) && (
              <div className="flex items-center justify-center gap-2.5 text-lg font-semibold">
                <TailwindSpinner className="mx-0 size-6 text-secondary" />
                {t('login-link.page.authenticating.title')}
              </div>
            )}

            {/* Error message */}
            {formState.errorText && (
              <div>
                <ExclamationCircleIcon className={iconErrorClass} />

                <h2 className="text-red! text-2xl font-semibold tracking-tight">
                  {t('login-link.page.error.title')}
                </h2>

                <div className="text-md lg:-mx-5">
                  <p className="mb-3">
                    {formState.errorText}
                  </p>

                  <LinkStyled
                    size="sm"
                    color="primary"
                    styling="text"
                    className="font-semibold"
                    href="/forgot-password"
                  >
                    {t('login-link.page.get-new-link')}
                  </LinkStyled>
                </div>
              </div>
            )}

            {/* Success message */}
            {formState.success && (
              <div>
                <CheckCircleIcon className={iconSuccessClass} />

                <h2 className="text-2xl font-semibold tracking-tight">
                  {t('global.label.success')}
                </h2>

                <div className="text-md lg:-mx-5">
                  <p className="mb-3">
                    {t('global.success.msg')}
                  </p>
                </div>
              </div>
            )}
          </>
        );
      }}
    </BaseAuthForm>
  );
};
